import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const categoryLabels = {
  actualites: 'Actualités',
  histoire: 'Histoire',
  culture: 'Culture',
  politique: 'Politique',
  sport: 'Sport',
  justice: 'Justice'
};

// Default fallback image - VersaillesPlus logo
const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_versailles-actu/artifacts/x061u1gg_logo%20Versailles%2B.jpeg";

// Handle image error by replacing with default logo
const handleImageError = (e) => {
  e.target.onerror = null; // Prevent infinite loop
  e.target.src = DEFAULT_IMAGE;
  e.target.className = "w-full h-full object-contain p-8 bg-stone-100";
};

// Badge for archive articles (from PDF)
const ArchiveBadge = ({ article }) => {
  if (article.generation_type !== 'pdf') return null;
  
  const label = article.journal_numero && article.journal_date 
    ? `${article.journal_numero} • ${article.journal_date}`
    : article.journal_numero || article.journal_date || 'Archives';
  
  return (
    <span className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-medium rounded-sm mr-2">
      📰 {label}
    </span>
  );
};

export const ArticleCard = ({ article, variant = 'default' }) => {
  const formattedDate = article.created_at 
    ? formatDistanceToNow(new Date(article.created_at), { addSuffix: true, locale: fr })
    : '';

  if (variant === 'hero') {
    return (
      <article className="relative group" data-testid={`article-hero-${article.id}`}>
        <Link to={`/article/${article.slug}`} className="block">
          <div className="article-image-container relative aspect-[16/10] bg-stone-200 rounded-sm overflow-hidden">
            {article.image_url ? (
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={handleImageError}
              />
            ) : (
              <img
                src={DEFAULT_IMAGE}
                alt="VersaillesPlus"
                className="w-full h-full object-contain p-8 bg-stone-100"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            
            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-3">
                <span className="category-label text-red-400">
                  {categoryLabels[article.category] || article.category}
                </span>
                {article.generation_type === 'pdf' && (article.journal_numero || article.journal_date) && (
                  <span className="inline-flex items-center px-2 py-0.5 bg-amber-500/90 text-white text-[10px] font-medium rounded-sm">
                    📰 {article.journal_numero} {article.journal_date && `• ${article.journal_date}`}
                  </span>
                )}
              </div>
              <h2 className="font-playfair text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight group-hover:text-red-300 transition-colors duration-200">
                {article.title}
              </h2>
              <p className="font-lora text-stone-300 mt-3 text-sm sm:text-base line-clamp-2">
                {article.excerpt}
              </p>
              <div className="flex items-center mt-4 text-xs text-stone-400 font-inter">
                <span>{article.author}</span>
                <span className="mx-2">•</span>
                <span>{formattedDate}</span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group" data-testid={`article-compact-${article.id}`}>
        <Link to={`/article/${article.slug}`} className="flex gap-4">
          <div className="article-image-container w-24 h-24 flex-shrink-0 bg-stone-200 rounded-sm overflow-hidden">
            {article.image_url ? (
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={handleImageError}
              />
            ) : (
              <img
                src={DEFAULT_IMAGE}
                alt="VersaillesPlus"
                className="w-full h-full object-contain p-2 bg-stone-100"
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center flex-wrap gap-1">
              <span className="category-label text-red-800 text-[10px]">
                {categoryLabels[article.category] || article.category}
              </span>
              <ArchiveBadge article={article} />
            </div>
            <h3 className="font-playfair text-base font-semibold text-stone-900 leading-tight mt-1 group-hover:text-red-800 transition-colors duration-200 line-clamp-2">
              {article.title}
            </h3>
            <p className="font-inter text-xs text-stone-500 mt-1">
              {formattedDate}
            </p>
          </div>
        </Link>
      </article>
    );
  }

  // Default variant
  return (
    <article className="group" data-testid={`article-card-${article.id}`}>
      <Link to={`/article/${article.slug}`} className="block">
        <div className="article-image-container aspect-[4/3] bg-stone-200 rounded-sm overflow-hidden">
          {article.image_url ? (
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={handleImageError}
            />
          ) : (
            <img
              src={DEFAULT_IMAGE}
              alt="VersaillesPlus"
              className="w-full h-full object-contain p-6 bg-stone-100"
            />
          )}
        </div>
        <div className="mt-4">
          <div className="flex items-center flex-wrap gap-1">
            <span className="category-label text-red-800">
              {categoryLabels[article.category] || article.category}
            </span>
            <ArchiveBadge article={article} />
          </div>
          <h3 className="font-playfair text-xl font-semibold text-stone-900 leading-tight mt-2 group-hover:text-red-800 transition-colors duration-200">
            {article.title}
          </h3>
          <p className="font-lora text-stone-600 text-sm mt-2 line-clamp-2">
            {article.excerpt}
          </p>
          <div className="flex items-center mt-3 text-xs text-stone-500 font-inter">
            <span>{article.author}</span>
            <span className="mx-2">•</span>
            <span>{formattedDate}</span>
          </div>
        </div>
      </Link>
    </article>
  );
};
